import React, { useCallback, useEffect, useState } from 'react';
import { ListActionSelect, useToggleState } from '@sweetgreen/garnish';

import {
  CreditCardAddModal,
  SingleOptionCreditCardPicker,
} from '@order/components';
import type { CardType } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { CREDIT_CARDS_ICONS } from '../../../../../constants/credit-cards';
import type { CreditCardOption } from '../../../Checkout.utils';
import { useCheckoutContext } from '../../../CheckoutProvider';
import { useCreditCardOptions } from '../../../hooks';

export const ConnectedCheckoutPaymentBlockCreditCardPicker = () => {
  const context = useCheckoutContext();

  return <CheckoutPaymentBlockCreditCardPicker {...context} />;
};

export const CheckoutPaymentBlockCreditCardPicker = (
  props: CreditCardPickerProps,
) => {
  const {
    customer,
    payload,
    checkoutFieldsValidation,
    handleCreditCardSelected,
    resetCheckoutFieldValidation,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();

  // ─── Credit Card Options ─────────────────────────────────────────────

  const { creditCardOptions } = useCreditCardOptions(customer);
  const creditCard = payload?.billingAccountId;
  const selectedCard = useCardByValueOrFirst(creditCard, creditCardOptions);
  const selectedCardIcon = CREDIT_CARDS_ICONS[selectedCard?.cardType];

  // ─── Automatically Select First Credit Card If None Are Selected ─────

  useEffect(() => {
    if (creditCard !== 'ADD' || !selectedCard) return;
    handleCreditCardSelected(selectedCard.value);
  }, [selectedCard, creditCard, handleCreditCardSelected]);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const {
    value: isShowingAddPaymentMethodModal,
    toggleOn: showAddPaymentMethodModal,
    toggleOff: hideAddPaymentMethodModal,
  } = useToggleState();

  const handleSelectedCardChange = useCallback(
    (cardId: string | number) => {
      resetCheckoutFieldValidation('billingAccountId');
      handleCreditCardSelected(cardId as string);
      const isAddCardOptionSelected = cardId === 'ADD';

      if (isAddCardOptionSelected) showAddPaymentMethodModal();
    },
    [
      resetCheckoutFieldValidation,
      handleCreditCardSelected,
      showAddPaymentMethodModal,
    ],
  );

  const onAddCard = useCallback(() => {
    handleSelectedCardChange('ADD');
  }, [handleSelectedCardChange]);

  // ─── Required Credit Card Notice ─────────────────────────────────────

  const isSelectCardFieldValid = checkoutFieldsValidation.billingAccountId;
  const selectCardFieldNotice = isSelectCardFieldValid
    ? undefined
    : t('checkout.required-field');

  const shouldShowSingleOptionCreditCardPicker = creditCardOptions.length === 1;

  return (
    <>
      {shouldShowSingleOptionCreditCardPicker ? (
        <SingleOptionCreditCardPicker
          notice={selectCardFieldNotice}
          onPress={onAddCard}
        />
      ) : (
        <ListActionSelect
          noticePalette="caution"
          submitBtnLabel={t('checkout.card-picker.submit')}
          notice={selectCardFieldNotice}
          iconName={selectedCardIcon}
          value={selectedCard.value}
          options={creditCardOptions}
          onSelect={handleSelectedCardChange}
        />
      )}

      <CreditCardAddModal
        visible={isShowingAddPaymentMethodModal}
        onRequestClose={hideAddPaymentMethodModal}
      />
    </>
  );
};

// ─── Hooks ─────────────────────────────────────────────────────────────

/**
 * If we already have selected a credit card, use that one.
 * Otherwise, use the second one, since the first is "Add card".
 * If there are no available options, use the "Add card" option.
 */
const useCardByValueOrFirst = (
  creditCard: string,
  creditCardOptions: readonly CreditCardOption[],
) => {
  const { t } = useLocalizationContext();
  const [selectedCard, setSelectedCard] = useState({
    value: 'ADD',
    label: t('checkout.add-card'),
    cardType: 'NEW' as CardType | 'NEW',
  });

  useEffect(() => {
    const cardToFind = creditCard === 'ADD' ? '' : creditCard;

    setSelectedCard(
      creditCardOptions.find(({ value }) => value === cardToFind) ??
        creditCardOptions[1] ??
        creditCardOptions[0],
    );
  }, [creditCardOptions, creditCard, setSelectedCard]);

  return selectedCard;
};

// ─── Types ─────────────────────────────────────────────────────────────

type CreditCardPickerProps = Pick<
  ReturnType<typeof useCheckoutContext>,
  | 'customer'
  | 'payload'
  | 'checkoutFieldsValidation'
  | 'handleCreditCardSelected'
  | 'resetCheckoutFieldValidation'
>;
